import { sortBy } from 'lodash'
import { Component, memo } from 'react'
import ReactCountryFlag from 'react-country-flag'
import onClickOutside from 'react-onclickoutside'
import styled from 'styled-components'

import arrow from '../../styles/assets/arrows/caret_down.svg'
import { mediaQueries, palette } from '../../styles/variables'
import phoneCodes from '../../utils/phoneCodes.json'
import { translate } from '../../utils/translation'
import { ArrowIcon } from '../icon/Icon'

const DropDownSelectorWrapper = styled.div((props) => ({
  position: 'relative',
  zIndex: 'auto',
  cursor: 'pointer',
  border: props.noBorder ? 'none' : `1px solid${palette.lightGray}`,
  margin: props.margin || '0 0 10px 0',
  width: props.width ? props.width : '100%',
  borderRadius: '2px',
  [mediaQueries.smallMobile]: {
    width: props.mobileWidth ? props.mobileWidth : '100%',
  },
}))

const SelectedOption = styled.div((props) => {
  if (props.opened) {
    return {
      borderRadius: '2px 2px 0 0',
      backgroundColor: props.backgroundColor || palette.white,
      color: palette.darkGray70,
      boxShadow: '0px 10px 15px 5px rgba(19,23,35,0.04)',
      height: '48px',
      '& .arrow': {
        transform: 'translate(6px, 1px) rotate(135deg)',
      },
      '& .caret': {
        transform: 'rotate(180deg)',
      },
      '& ~ div': {
        borderRadius: '0px 2px 2px 2px',
      },
      [mediaQueries.mobile]: {
        display: 'flex',
        alignItems: 'center',
        margin: 0,
      },
    }
  }
  return {
    borderRadius: '2px',
    color: palette.darkGray,
    backgroundColor: props.backgroundColor || palette.lightGray10,
    height: '48px',
    [mediaQueries.mobile]: {
      display: 'flex',
      alignItems: 'center',
      margin: 0,
    },
  }
})

const OptionWrapper = styled.div((props) => ({
  display: 'flex',
  height: '48px',
  width: '100%',
  padding: props.padding || '0 20px',
  alignItems: 'center',
  [mediaQueries.mobile]: {
    width: '100%',
  },
}))

const OptionName = styled.div({
  fontSize: '15px',
  flex: 1,
  height: '48px',
  width: '100%',
  lineHeight: '50px',
  marginLeft: '20px',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
})

const OptionDropdown = styled.div({
  position: 'absolute',
  zIndex: 100,
  marginTop: '4px',
  width: 'max-content',
  background: palette.white,
  boxShadow: '0px 14px 15px 5px rgba(19,23,35,0.04)',
  borderRadius: '2px',
  overflow: 'auto',
  [mediaQueries.mobile]: {
    width: '250px',
    marginLeft: 0,
  },
})

const OptionList = styled.div((props) => ({
  width: '100%',
  maxHeight: '190px',
  maxWidth: props.dropdownMaxWidth ? props.dropdownMaxWidth : 'none',
  overflowY: 'auto',
  '& div': {
    color: palette.darkGray50,
  },
  '& div:hover': {
    color: palette.darkGray70,
    background: palette.lightGray20,
    cursor: 'pointer',
  },
  [mediaQueries.mobile]: {
    width: '100%',
  },
}))

const Option = memo((props) => (
  <OptionWrapper onClick={props.onClick} padding={props.padding}>
    {props.countryCode && (
      <ReactCountryFlag
        countryCode={props.countryCode}
        svg
        style={{
          marginTop: '-3px',
          width: '1.3333em',
          height: '1em',
          ...(props.flagStyle || {}),
        }}
      />
    )}
    {!props.hideLabel && <OptionName>{props.name}</OptionName>}
    {!props.hideLabel && <div>{props.value}</div>}
    {props.showArrow &&
      (props.caretType === 'secondary' ? (
        <img
          src={arrow}
          className="caret"
          style={{ width: '6px', height: '4px', margin: '6px' }}
          alt="arrow"
        />
      ) : (
        <ArrowIcon down className="arrow" color={palette.darkGray} />
      ))}
  </OptionWrapper>
))

export const getDialCode = (countryCode) =>
  (phoneCodes.find((item) => item.code === countryCode) || { dial_code: '+1' })
    .dial_code

const countryCodeOptions = sortBy(
  phoneCodes.map((item) => ({
    id: item.code,
    label: `${translate(item.name)} ${item.dial_code}`,
    name: translate(item.name),
    value: item.dial_code,
  })),
  (item) => item.label,
)

const getDefaultProps = ({
  onChange = null,
  value = '',
  width = '100%',
  contentWidth = undefined,
  hideLabel = false,
  noBorder = false,
  margin = '0 0 10px 0',
  padding = '0 20px',
  backgroundColor = undefined,
  flagStyle = {},
  caretType = 'primary',
  mobileWidth = '100%',
  dropdownMaxWidth = undefined,
  placeholder = '',
}) => {
  return {
    onChange,
    value,
    width,
    contentWidth,
    hideLabel,
    noBorder,
    margin,
    padding,
    backgroundColor,
    flagStyle,
    caretType,
    mobileWidth,
    dropdownMaxWidth,
    placeholder,
  }
}
class DropdownSelector extends Component {
  constructor(props) {
    super(props)
    const { value } = getDefaultProps(props)
    let currentOption = null
    if (value) {
      currentOption = countryCodeOptions.find((o) => o.id === value)
    }
    this.state = {
      isOpen: false,
      currentOption,
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      prevState.currentOption === null ||
      nextProps.value !== prevState.currentOption.value
    ) {
      const currentOption = countryCodeOptions.find(
        (o) => o.id === nextProps.value,
      )
      return { currentOption }
    }
    return null
  }

  // We need this disabled because the handleClickOutside is actually used by the react-onclickoutside library
  // eslint-disable-next-line react/no-unused-class-component-methods
  handleClickOutside = () => {
    this.setState({ isOpen: false })
  }

  selectOption = (option) => {
    this.setState({
      isOpen: false,
      currentOption: option,
    })
    if (this.props.onChange) {
      this.props.onChange(option.id)
    }
  }

  openList = () => {
    this.setState((prevState) => ({ isOpen: !prevState.isOpen }))
  }

  render() {
    const { isOpen, currentOption } = this.state
    const {
      placeholder,
      width,
      contentWidth,
      hideLabel,
      noBorder,
      margin,
      padding,
      backgroundColor,
      flagStyle,
      caretType,
      mobileWidth,
      dropdownMaxWidth,
    } = getDefaultProps(this.props)

    if (isOpen) {
      return (
        <DropDownSelectorWrapper
          width={width}
          mobileWidth={mobileWidth}
          opened
          noBorder={noBorder}
          margin={margin}
        >
          <SelectedOption
            onClick={this.openList}
            backgroundColor={backgroundColor}
            opened
          >
            {currentOption ? (
              <Option
                value={currentOption.value}
                label={currentOption.value}
                countryCode={currentOption.id}
                showArrow
                hideLabel={hideLabel}
                padding={padding}
                flagStyle={flagStyle}
                caretType={caretType}
              />
            ) : (
              <Option
                value={null}
                label={placeholder}
                showArrow
                hideLabel={hideLabel}
                padding={padding}
                flagStyle={flagStyle}
                caretType={caretType}
              />
            )}
          </SelectedOption>
          <OptionDropdown width={contentWidth}>
            <OptionList dropdownMaxWidth={dropdownMaxWidth}>
              {countryCodeOptions.map((item, index) => (
                <Option
                  key={item.id || `option-${index}`}
                  value={item.value}
                  label={item.label}
                  name={item.name}
                  countryCode={item.id}
                  onClick={() => this.selectOption(item)}
                />
              ))}
            </OptionList>
          </OptionDropdown>
        </DropDownSelectorWrapper>
      )
    }
    return (
      <DropDownSelectorWrapper
        width={width}
        mobileWidth={mobileWidth}
        noBorder={noBorder}
        margin={margin}
      >
        <SelectedOption
          onClick={this.openList}
          backgroundColor={backgroundColor}
        >
          {currentOption ? (
            <Option
              value={currentOption.value}
              label={currentOption.value}
              countryCode={currentOption.id}
              showArrow
              hideLabel={hideLabel}
              padding={padding}
              flagStyle={flagStyle}
              caretType={caretType}
            />
          ) : (
            <Option
              value={null}
              label={placeholder}
              showArrow
              hideLabel={hideLabel}
              padding={padding}
              flagStyle={flagStyle}
              caretType={caretType}
            />
          )}
        </SelectedOption>
      </DropDownSelectorWrapper>
    )
  }
}

export default onClickOutside(DropdownSelector)
